import React, { useState, useEffect, useRef } from 'react';
import { ReportCriteria } from './ReportCriteria';
import { Archive, BetweenVerticalStart, Blend, ChartBarIncreasing, ChevronDown, ClipboardList, Clock, FileDigit, FileText, FileType2, HardDriveDownload, List, Plus, Search, Share, SquarePen, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ReportList } from './ReportList';
import ReportColumns from './ReportColumns';
import { ReportView } from './ReportView';
import { ReportSave } from './ReportSave';
import { ListReportCases } from './ListReportCases';
import Modal from './Modal';
import * as XLSX from 'xlsx'; // retained for other exports (CSV/Word prep) if needed
import ExcelJS from 'exceljs';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import Alert from './Alert';
import { useNavVariant } from '../App';
import { canAccessPublicForms, PUBLIC_FORMS_PATH } from '../utils/publicFormsAccess';
import { buildManagedPublicFormReportRequest, isManagedPublicFormReport } from '../utils/publicFormReports';
import { neutralizeSpreadsheetCell, quoteCsvCell } from '../utils/csvExport';
import { canUseCustomFieldInDataReport } from '../utils/matterPublicForms';
import { mergeSavedReportCustomFields } from '../utils/reportSavedFields';

const parseReportCriteria = (criteria) => {
    if (!criteria) return null;
    if (typeof criteria === 'object') return criteria;

    try {
        return JSON.parse(criteria);
    } catch (error) {
        return null;
    }
};

const isListReportDefinition = (report) => {
    if (!report) return false;
    if (report._reportMode === 'list') return true;
    return parseReportCriteria(report.criteria)?.report_mode === 'list';
};

export const isPublicFormReportDefinition = isManagedPublicFormReport;

const parseReportFields = (fields) => {
    if (Array.isArray(fields)) return fields;
    if (typeof fields !== 'string' || !fields.trim()) return [];
    try {
        const parsed = JSON.parse(fields);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

export const normalizePublicFormReportFields = (fields) => parseReportFields(fields)
    .map((field) => {
        if (typeof field === 'string' || typeof field === 'number') {
            return { id: String(field), name: String(field), edit: 0, section_id: '[public-form]' };
        }
        if (!field || typeof field !== 'object') return null;
        const id = field.id ?? field.uid ?? field.field_key;
        if (id === null || id === undefined || String(id).trim() === '') return null;
        return {
            ...field,
            id: String(id),
            name: String(field.name ?? field.label ?? id),
            edit: 0,
            section_id: '[public-form]',
        };
    })
    .filter(Boolean);

const ReportProgress = ({ selectedStep, setSelectedStep, selectedReport, isListReport }) => {
    const steps = [
        "Select Report",
        "Set Up Columns",
        isListReport ? "Select Cases" : "Set Criteria",
        "Run Report",
        "Save/Export"
    ];
    const stepIcons = [
        List,
        BetweenVerticalStart,
        isListReport ? Search : Blend,
        ChartBarIncreasing,
        HardDriveDownload
    ];
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className='report-progress'>
            {screenWidth > 1000 ? steps.map((s, index) => (
                <React.Fragment key={index}>
                    <div
                        className='progress-step-container'
                        key={index}
                        onClick={() => {
                            if (selectedReport) 
                                setSelectedStep(index);
                        }}
                    >
                        <div className={`progress-circle  ${index === selectedStep ? 'active' : ''} ${index < selectedStep ? 'completed' : ''}`}>
                            {React.createElement(stepIcons[index], { size: 18})}
                        </div>
                        <div className={`progress-step subtext ${index === selectedStep ? 'active' : ''}`}>
                            {s}
                        </div>
                    </div>
                    {index + 1 < steps.length && 
                        <div className='horizontal-div-container'>
                            <div className='divider horizontal' key={`divider-${index}`}></div>
                        </div>
                    }
                </React.Fragment>
            )) : (
                <div className='filter-group' style={{ display: 'flex', alignItems: 'center' }}>
                    <div className='select-wrapper'>
                        <select className='filter-select' value={selectedStep} onChange={(e) => setSelectedStep(Number(e.target.value))}>
                            {steps.map((s, index) => (
                                <option key={index} value={index}>
                                    {s}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="select-chevron" />
                    </div>
                </div>
            )}
        </div>
    )
}

const Reports = ({ accessLevel }) => {
    const initialUrlParams = new URLSearchParams(window.location.search);
    const navigate = useNavigate();
    const [reportMode, setReportMode] = useState('hub');
    const [selectedStep, setSelectedStep] = useState(0);
    const [selectedReport, setSelectedReport] = useState(null);
    const [customFields, setCustomFields] = useState([]);
    const [customFieldsData, setCustomFieldsData] = useState(null); // Store API response separately
    const [sections, setSections] = useState([]);
    const navVariant = useNavVariant();
    const [selectedFields, setSelectedFields] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [lookupMaps, setLookupMaps] = useState({});
    const [reports, setReports] = useState([]);
    const [fetchingReport, setFetchingReport] = useState(false);
    const [loadingCustomFields, setLoadingCustomFields] = useState(false);
    const [message, setMessage] = useState(null);
    const [selectedCriteria, setSelectedCriteria] = useState({
        logic: 'AND',
        conditions: []
    });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 250, 500, 1000]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [shareToken] = useState(initialUrlParams.get('share') || initialUrlParams.get('share_token') || '');
    const [shareReportId] = useState(initialUrlParams.get('id') || '');
    const [showRenameReport, setShowRenameReport] = useState(false);
    const [renameReportName, setRenameReportName] = useState('');
    const [reportSaveInitialAction, setReportSaveInitialAction] = useState(null);
    const reportValuesAbortRef = useRef(null);
    const reportValuesRequestIdRef = useRef(0);
    const selectedReportIdRef = useRef(null);
    const hubFetchTimeoutRef = useRef(null);
    const lastHubFetchKeyRef = useRef('');
    const autoOpenedRunReportRef = useRef(false);
    const isListReport = isListReportDefinition(selectedReport);
    const isPublicFormReport = isPublicFormReportDefinition(selectedReport);

    // Build the output key that matches backend column naming
    const normalizeFieldId = (fieldId) => {
        let id = fieldId;
        while (typeof id === 'object' && id !== null && id.id !== undefined) {
            id = id.id;
        }
        return id;
    };

    const getOutputKey = (field) => {
        if (!field) return '';
        return normalizeFieldId(field.id);
    };

    const formatDeadlineExportValue = (value) => {
        if (!value || Array.isArray(value) || typeof value !== 'object') {
            return null;
        }

        const due = typeof value.due === 'string' ? value.due : '';
        const done = typeof value.done === 'string' ? value.done : '';

        if (due === '' && done === '') {
            return null;
        }

        return `Due: ${due}, Done: ${done}`;
    };

    const formatMultiValueExportValue = (value) => {
        if (!value || Array.isArray(value) || typeof value !== 'object' || value.type !== 'multi_value') {
            return null;
        }

        if (typeof value.display === 'string' && value.display.trim() !== '') {
            return value.display;
        }

        if (Array.isArray(value.rows)) {
            return value.rows
                .map((row) => row?.display || '')
                .filter(Boolean)
                .join('\n');
        }

        return '';
    };

    const getExportDisplayValue = (row, field, key) => {
        if (field?.isBlank) {
            return '';
        }

        let value = row?.[key];

        if (field?.dropdownReturn === 'id' && row?._rawData && row._rawData[key] !== undefined) {
            value = row._rawData[key];
        }

        if (field?.contactProperty && value && typeof value === 'object' && !Array.isArray(value)) {
            value = value[field.contactProperty] ?? value.full_name ?? value;
        }

        const formattedMultiValue = formatMultiValueExportValue(value);
        if (formattedMultiValue !== null) {
            return formattedMultiValue;
        }

        const formattedDeadline = formatDeadlineExportValue(value);
        if (formattedDeadline !== null) {
            return formattedDeadline;
        }

        if (Array.isArray(value)) {
            return value.map((item) => {
                if (item === null || item === undefined || item === '') return '';
                if (typeof item !== 'object') return String(item);
                return String(item.file_name ?? item.name ?? item.label ?? item.display ?? item.value ?? 'Attached file');
            }).filter(Boolean).join(', ');
        }

        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return value;
    };

    const tableFields = [
        { id: 'cases.id', name: 'Case ID', edit: 0, section_id: '[-1]' },
        { id: 'cases.case_name', name: 'Case Name', edit: 1, section_id: '[-1]' },
        { id: 'cases.contact_id', name: 'Client', field_id: 8, edit: 1, section_id: '[-1]' },
        { id: 'cases.lead_id', name: 'Lead ID', edit: 1, section_id: '[-1]' },
        { id: 'cases.template_id', name: 'Case Template', edit: 1, section_id: '[-1]' },
        { id: 'cases.phase_id', name: 'Case Phase', edit: 1, section_id: '[-1]' },
        { id: 'cases.subphase_id', name: 'Case Subphase', edit: 1, section_id: '[-1]' },
        { id: "cases.team['1'][0]", name: 'First Primary', edit: 1, section_id: '[-1]' },
        { id: 'cases.create_date', name: 'Creation Date', edit: 0, section_id: '[-1]' },
        { id: 'cases.updated_at', name: 'Last Modified', edit: 0, section_id: '[-1]' },
        { id: 'cases.created_by', name: 'Created By', edit: 0, section_id: '[-1]' },
        { id: 'cases.tags', name: 'Hashtags', edit: 1, section_id: '[-1]' },
        { id: 'cases.unique_email', name: 'Unique Email', edit: 0, section_id: '[-1]' },
        { id: 'cases.test_case', name: 'Test Case', edit: 1, section_id: '[-1]' },
        { id: 'cases.pfs', name: 'Uber PFS Case', edit: 1, section_id: '[-1]' },
        { id: 'computed.team_member', name: 'Team Member', edit: 0, section_id: '[-1]' },
        { id: 'leads.case_likelihood', name: 'Case Likelihood', edit: 1, section_id: '[-1]' },
        { id: 'leads.case_type_id', name: 'Case Type', edit: 1, section_id: '[-1]' },
        { id: 'leads.referral_contact', name: 'Referral Contact', field_id: 8, edit: 1, section_id: '[-1]' },
        { id: 'leads.db_origination', name: 'DB Origination', field_id: 8, edit: 1, section_id: '[-1]' },
        { id: 'leads.marketing_source', name: 'Marketing Source', edit: 1, section_id: '[-1]' },
        { id: 'leads.marketing_lead', name: 'Marketing Lead', edit: 1, section_id: '[-1]' },
        // Client details fields for case contact_details via cases.contact_id
        { id: 'contacts.phone_numbers', name: 'Client Phone Numbers (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.phone_number', name: 'Client Phone Number (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.emails', name: 'Client Emails (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.email', name: 'Client Email (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.addresses', name: 'Client Addresses (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address', name: 'Client Address (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_line1', name: 'Client Address Line 1', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_line2', name: 'Client Address Line 2', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_city', name: 'Client Address City', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_state', name: 'Client Address State', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_postal_code', name: 'Client Address Postal Code', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address_type', name: 'Client Address Type', edit: 0, section_id: '[-3]' },
        // Team Section - First person in each role
        { id: "cases.team['1'][0]", name: 'First Primary', edit: 1, section_id: '[-2]' },
        { id: "cases.team['2'][0]", name: 'First Attorney', edit: 1, section_id: '[-2]' },
        { id: "cases.team['3'][0]", name: 'First Paralegal', edit: 1, section_id: '[-2]' },
        { id: "cases.team['4'][0]", name: 'First Intake Specialist', edit: 1, section_id: '[-2]' },
        { id: "cases.team['5'][0]", name: 'First Settlement', edit: 1, section_id: '[-2]' },
        { id: "cases.team['6'][0]", name: 'First Accounting - Finance Manager', edit: 1, section_id: '[-2]' },
        { id: "cases.team['7'][0]", name: 'First Accounts Payable', edit: 1, section_id: '[-2]' },
        { id: "cases.team['8'][0]", name: 'First Litigation Director', edit: 1, section_id: '[-2]' },
        { id: "cases.team['9'][0]", name: 'First Legal Assistant', edit: 1, section_id: '[-2]' },
        { id: "cases.team['10'][0]", name: 'First Administrative Assistant', edit: 1, section_id: '[-2]' },
        { id: "cases.team['11'][0]", name: 'First Firm Administrator', edit: 1, section_id: '[-2]' },
        { id: "cases.team['12'][0]", name: 'First Receptionist', edit: 1, section_id: '[-2]' },
        { id: "cases.team['13'][0]", name: 'First Case Manager', edit: 1, section_id: '[-2]' },
        { id: "cases.team['14'][0]", name: 'First Law Clerk', edit: 1, section_id: '[-2]' },
        { id: "cases.team['15'][0]", name: 'First Intake Review', edit: 1, section_id: '[-2]' },
        { id: "cases.team['16'][0]", name: 'First Director of Training', edit: 1, section_id: '[-2]' },
    ];

    const normalizeReportField = (field) => {
        if (String(field?.id || '').startsWith('item_created_date_')) {
            return { ...field, field_id: 6 };
        }
        return field;
    };

    const getContactFields = () => [
        { id: 'contacts.id', name: 'Contact ID', edit: 0, section_id: '[-1]' },
        { id: 'contacts.full_name', name: 'Contact', edit: 1, section_id: '[-1]' },
        { id: 'contacts.first_name', name: 'First Name', edit: 1, section_id: '[-1]' },
        { id: 'contacts.middle_name', name: 'Middle Name', edit: 1, section_id: '[-1]' },
        { id: 'contacts.last_name', name: 'Last Name', edit: 1, section_id: '[-1]' },
        { id: 'contacts.nickname', name: 'Nickname', edit: 1, section_id: '[-1]' },
        { id: 'contacts.prefix', name: 'Prefix', edit: 1, section_id: '[-1]' },
        { id: 'contacts.suffix', name: 'Suffix', edit: 1, section_id: '[-1]' },
        { id: 'contacts.salutation', name: 'Salutation', edit: 1, section_id: '[-1]' },
        { id: 'contacts.company_name', name: 'Company Name', edit: 1, section_id: '[-1]' },
        { id: 'contacts.job_title', name: 'Job Title', edit: 1, section_id: '[-1]' },
        { id: 'contacts.department', name: 'Department', edit: 1, section_id: '[-1]' },
        { id: 'contacts.date_of_birth', name: 'Date of Birth', edit: 1, section_id: '[-1]' },
        { id: 'contacts.date_of_death', name: 'Date of Death', edit: 1, section_id: '[-1]' },
        { id: 'contacts.is_deceased', name: 'Is Deceased', edit: 1, section_id: '[-1]' },
        { id: 'contacts.is_company', name: 'Is Company', edit: 1, section_id: '[-1]' },
        { id: 'contacts.contact_type', name: 'Contact Type', edit: 1, section_id: '[-1]' },
        { id: 'contacts.gender', name: 'Gender', edit: 1, section_id: '[-1]' },
        { id: 'contacts.preferred_contact_method', name: 'Preferred Contact Method', edit: 1, section_id: '[-1]' },
        { id: 'contacts.opportunity', name: 'Opportunity', edit: 1, section_id: '[-1]' },
        { id: 'contacts.disregard', name: 'Disregard', edit: 1, section_id: '[-1]' },
        { id: 'contacts.disregard_reason', name: 'Disregard Reason', edit: 1, section_id: '[-1]' },
        { id: 'contacts.created_at', name: 'Created At', edit: 0, section_id: '[-1]' },
        { id: 'contacts.updated_at', name: 'Updated At', edit: 0, section_id: '[-1]' },
        { id: 'contacts.phone_numbers', name: 'Phone Numbers (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.phone_number', name: 'Phone Number (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.emails', name: 'Email Addresses (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.email', name: 'Email Address (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.addresses', name: 'Addresses (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address', name: 'Address (First)', edit: 0, section_id: '[-3]' },
        { id: 'contact_information.notes', name: 'Notes', edit: 1, section_id: '[-4]' },
        { id: 'contact_information.custom', name: 'Custom Information', edit: 1, section_id: '[-4]' },
    ];

    useEffect(() => {
        document.title = selectedReport ? `${selectedReport.name} | CaseDB` : "Reports | CaseDB";
        setRenameReportName(selectedReport?.name || '');
        if (!selectedReport) {
            setShowRenameReport(false);
        }
    }, [selectedReport]);

    const getTaskFields = () => [
        // Task fields from feed_updates
        { id: 'feed_updates.id', name: 'Task ID', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.subject', name: 'Task Subject', edit: 1, section_id: '[-1]' },
        { id: 'feed_updates.content', name: 'Task Content', edit: 1, section_id: '[-1]' },
        { id: 'feed_updates.author', name: 'Task Author', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.creation_date', name: 'Task Created', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.case_id', name: 'Case ID', edit: 0, section_id: '[-1]' },
        { id: 'cases.case_name', name: 'Case Name', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.lead_id', name: 'Lead ID', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.contact_id', name: 'Contact ID', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.task', name: 'Task Number', edit: 0, section_id: '[-1]' },
        { id: 'feed_updates.tags', name: 'Task Tags', edit: 1, section_id: '[-1]' },
        { id: 'feed_updates.metadata', name: 'Task Metadata', edit: 1, section_id: '[-1]' },
        
        // Deadline fields
        { id: 'deadlines.due', name: 'Due Date', edit: 1, section_id: '[-2]' },
        { id: 'deadlines.done', name: 'Completed Date', edit: 1, section_id: '[-2]' },
        { id: 'deadlines.assignees', name: 'Assignees', edit: 1, section_id: '[-2]' },
        { id: 'deadlines.created_by', name: 'Deadline Created By', edit: 0, section_id: '[-2]' },
        { id: 'deadlines.created_at', name: 'Deadline Created', edit: 0, section_id: '[-2]' },
    ];

    const getLeadFields = () => [
        { id: 'leads.id', name: 'Lead ID', edit: 0, section_id: '[-3]' },
        { id: 'leads.intake_by', name: 'Intake By', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.created_at', name: 'Created At', edit: 0, section_id: '[-3]' },
        { id: 'leads.contact_id', name: 'Lead Contact', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.case_type_id', name: 'Case Type', edit: 1, section_id: '[-3]' },
        { id: 'leads.incident_date', name: 'Incident Date', edit: 1, section_id: '[-3]' },
        { id: 'leads.marketing_source', name: 'Marketing Source', edit: 1, section_id: '[-3]' },
        { id: 'leads.marketing_lead', name: 'Marketing Lead', edit: 1, section_id: '[-3]' },
        { id: 'leads.referral_contact', name: 'Referral Contact', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.db_origination', name: 'DB Origination', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.marketing_notes', name: 'Marketing Notes', edit: 1, section_id: '[-3]' },
        { id: 'leads.summary', name: 'Summary', edit: 1, section_id: '[-3]' },
        { id: 'leads.case_likelihood', name: 'Case Likelihood', edit: 1, section_id: '[-3]' },
        { id: 'leads.office', name: 'Office', edit: 1, section_id: '[-3]' },
        { id: 'leads.assigned_to', name: 'Assigned To', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.assigned_to_attorney', name: 'Assigned Attorney', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.status', name: 'Status', edit: 1, section_id: '[-3]' },
        { id: 'leads.notes', name: 'Notes', edit: 1, section_id: '[-3]' },
        { id: 'leads.referred_to', name: 'Referred To', field_id: 8, edit: 1, section_id: '[-3]' },
        { id: 'leads.updated_at', name: 'Updated At', edit: 0, section_id: '[-3]' },
        { id: 'leads.status_date', name: 'Status Date', edit: 0, section_id: '[-3]' },
        { id: 'leads.deleted', name: 'Deleted', edit: 1, section_id: '[-3]' },
        // Contact fields for the lead's associated contact
        { id: 'contacts.full_name', name: 'Contact', edit: 0, section_id: '[-3]' },
        { id: 'contacts.first_name', name: 'Contact First Name', edit: 0, section_id: '[-3]' },
        { id: 'contacts.last_name', name: 'Contact Last Name', edit: 0, section_id: '[-3]' },
        // Contact detail fields
        { id: 'contacts.phone_numbers', name: 'Contact Phone Numbers (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.phone_number', name: 'Contact Phone Number (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.emails', name: 'Contact Emails (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.email', name: 'Contact Email (First)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.addresses', name: 'Contact Addresses (All)', edit: 0, section_id: '[-3]' },
        { id: 'contacts.address', name: 'Contact Address (First)', edit: 0, section_id: '[-3]' },
    ];

    const fetchFields = async () => {
        try {
            // Determine which fields to load based on report type
            const reportType = selectedReport?.report_type || 'cases';
            console.log('Report type:', reportType, 'Selected report:', selectedReport);
            let availableFields = [];
            let localCustomFieldsData = null;

            if (isPublicFormReportDefinition(selectedReport)) {
                availableFields = normalizePublicFormReportFields(selectedReport?.fields);
                setCustomFieldsData(null);
                setCustomFields(availableFields);
                setSelectedFields(availableFields);
                setSelectedCriteria(parseReportCriteria(selectedReport?.criteria) || {});
                setSections([{ id: 'public-form', name: 'Form fields', order_id: -1 }]);
                setLoadingCustomFields(false);
                return;
            }
            
            // Create abort controller for custom_fields fetch so we can cancel it if report type changes
            const customFieldsAbortController = new AbortController();
            
            // For cases and leads, fetch custom_fields.php ONCE at the beginning
            if (reportType === 'cases' || reportType === 'leads') {
                setLoadingCustomFields(true);
                try {
                    const response = await fetch('https://api.casedb.co/custom_fields.php', { 
                        credentials: 'include',
                        signal: customFieldsAbortController.signal
                    });
                    localCustomFieldsData = await response.json();
                } catch (error) {
                    // If it's an abort error, just exit silently - new fetch is already happening
                    if (error.name === 'AbortError') {
                        console.log('Custom fields fetch aborted due to report type change');
                        return;
                    }
                    throw error;
                }
                
                // RACE CONDITION CHECK: If report type changed while fetching, abort
                // This prevents old fetch results from overwriting newly-selected report type fields
                const currentReportType = selectedReport?.report_type || 'cases';
                if (currentReportType !== reportType) {
                    console.log(`Report type changed during custom_fields fetch (was ${reportType}, now ${currentReportType}) - aborting old fetch`);
                    customFieldsAbortController.abort();
                    return;
                }
                
                // Only store to state if we're still on a report type that needs it
                if (localCustomFieldsData?.all_custom_fields) {
                    localCustomFieldsData = {
                        ...localCustomFieldsData,
                        all_custom_fields: mergeSavedReportCustomFields(
                            localCustomFieldsData.all_custom_fields,
                            selectedReport?.id ? selectedReport.saved_custom_fields : []
                        )
                            .filter(canUseCustomFieldInDataReport)
                            .map(normalizeReportField)
                    };
                }

                setCustomFieldsData(localCustomFieldsData);
            }
            
            if (reportType === 'contacts') {
                availableFields = getContactFields();
                // Also load custom fields for contacts
                availableFields = [...availableFields, ...(localCustomFieldsData?.all_custom_fields || [])];
                console.log('Contact fields loaded:', availableFields.length, 'fields (base + custom)');
                setCustomFields(availableFields);
                
                // Handle saved fields for contacts
                if (selectedReport?.fields) {
                    const savedFieldData = typeof selectedReport.fields === 'string' 
                        ? JSON.parse(selectedReport.fields) 
                        : selectedReport.fields;
                    const orderedFields = savedFieldData.map(fieldData => {
                        if (typeof fieldData === 'string' && fieldData.startsWith('blank_')) {
                            const parts = fieldData.split('_');
                            let name = 'Blank Column';
                            if (parts.length >= 3) {
                                const encodedName = parts.slice(2).join('_');
                                try {
                                    name = decodeURIComponent(encodedName);
                                } catch (e) {
                                    name = encodedName.replace(/%20/g, ' ').replace(/%/g, '');
                                }
                            }
                            return { id: fieldData, name: name, isBlank: true, field_id: null, section_id: '[-1]' };
                        }
                        
                        // Extract field ID from various formats - recursively unwrap nested objects
                        let fieldId = (typeof fieldData === 'object' && fieldData !== null) ? fieldData.id : fieldData;
                        // Recursively unwrap if fieldId is still an object
                        while (typeof fieldId === 'object' && fieldId !== null && fieldId.id !== undefined) {
                            fieldId = fieldId.id;
                        }
                        
                        const resolvedFieldId = normalizeFieldId(fieldId);
                        const foundField = availableFields.find(f => String(f.id) === String(resolvedFieldId));
                        
                        if (foundField) {
                            return {
                                ...foundField,
                                id: resolvedFieldId,  // Use the unwrapped primitive field ID
                                name: (typeof fieldData === 'object' && fieldData.name) || foundField.name,
                                ...(typeof fieldData === 'object' && fieldData.contactProperty && { contactProperty: fieldData.contactProperty }),
                                ...(typeof fieldData === 'object' && fieldData.dropdownReturn && { dropdownReturn: fieldData.dropdownReturn })
                            };
                        }
                        
                        return foundField || { 
                            id: resolvedFieldId, 
                            name: (typeof fieldData === 'object' && fieldData.name) || `Field ${resolvedFieldId}` 
                        };
                    });
                    setSelectedFields(orderedFields);
                } else {
                    setSelectedFields([]);
                }
                
                // Handle saved criteria for contacts
                if (selectedReport?.criteria) {
                    const parsedCriteria = typeof selectedReport.criteria === 'string' ? JSON.parse(selectedReport.criteria) : selectedReport.criteria;
                    setSelectedCriteria(parsedCriteria);
                } else {
                    setSelectedCriteria({ logic: 'AND', conditions: [] });
                }
                
                // Set sections for contacts
                const contactSections = [
                    { id: -1, name: 'General', order_id: -1 },
                    { id: -3, name: 'Contact Details', order_id: -3 },
                    { id: -4, name: 'Contact Information', order_id: -4 }
                ];
                if (localCustomFieldsData?.sections) {
                    contactSections.push(...localCustomFieldsData.sections);
                }
                setSections(contactSections);
                return;
            } else if (reportType === 'tasks') {
                // Tasks only use feed_updates, deadlines, and cases fields - no custom fields needed
                // Abort any pending custom_fields fetch
                customFieldsAbortController.abort();
                
                availableFields = getTaskFields();
                console.log('Task fields loaded:', availableFields.length, 'fields:', availableFields.map(f => f.id));
                setCustomFields(availableFields);
                if (selectedReport?.fields) {
                    const savedFieldData = typeof selectedReport.fields === 'string' 
                        ? JSON.parse(selectedReport.fields) 
                        : selectedReport.fields;
                    const orderedFields = savedFieldData.map(fieldData => {
                        if (typeof fieldData === 'string' && fieldData.startsWith('blank_')) {
                            const parts = fieldData.split('_');
                            let name = 'Blank Column';
                            if (parts.length >= 3) {
                                const encodedName = parts.slice(2).join('_');
                                try {
                                    name = decodeURIComponent(encodedName);
                                } catch (e) {
                                    name = encodedName.replace(/%20/g, ' ').replace(/%/g, '');
                                }
                            }
                            return { id: fieldData, name: name, isBlank: true, field_id: null, section_id: '[-1]' };
                        }
                        const resolvedFieldId = normalizeFieldId(fieldData?.id ?? fieldData);
                        const foundField = availableFields.find(f => String(f.id) === String(resolvedFieldId));
                        return foundField || { id: resolvedFieldId, name: fieldData.name || `Field ${resolvedFieldId}` };
                    });
                    setSelectedFields(orderedFields);
                } else {
                    setSelectedFields([]);
                }
                if (selectedReport?.criteria) {
                    const parsedCriteria = typeof selectedReport.criteria === 'string' ? JSON.parse(selectedReport.criteria) : selectedReport.criteria;
                    setSelectedCriteria(parsedCriteria);
                } else {
                    setSelectedCriteria({ logic: 'AND', conditions: [] });
                }
                setSections([
                    { id: -1, name: 'Task Fields', order_id: -1 },
                    { id: -2, name: 'Deadline Fields', order_id: -2 }
                ]);
                return;
            } else if (reportType === 'leads') {
                availableFields = getLeadFields();
                console.log('Lead fields loaded:', availableFields);
                // Also load custom fields and case table fields for leads
                availableFields = [...availableFields, ...(localCustomFieldsData?.all_custom_fields || []), ...tableFields];
                console.log('Total fields after adding custom fields and case fields:', availableFields.length);
                setCustomFields(availableFields);
            } else {
                // Load custom fields for case reports
                availableFields = [...(localCustomFieldsData?.all_custom_fields || []), ...tableFields];
                console.log('Case fields loaded - custom fields:', localCustomFieldsData?.all_custom_fields?.length || 0, 'table fields:', tableFields.length);
                if (localCustomFieldsData?.all_custom_fields?.length > 0) {
                    console.log('Sample custom field IDs:', localCustomFieldsData.all_custom_fields.slice(0, 10).map(f => ({ id: f.id, type: typeof f.id, name: f.name })));
                }
                setCustomFields(availableFields);
            }
            
            console.log('Total availableFields:', availableFields.length);
            
            // Skip field restoration for tasks and contacts - they handle it in their branches above
            const reportType2 = selectedReport?.report_type || 'cases';
            if (reportType2 === 'tasks' || reportType2 === 'contacts') {
                return;
            }
            
            // Preserve the exact order from the saved report (for cases and leads only)
            if (selectedReport?.fields) {
                const savedFieldData = typeof selectedReport.fields === 'string' 
                    ? JSON.parse(selectedReport.fields) 
                    : selectedReport.fields;
                
                console.log('Loading saved fields:', savedFieldData);
                
                // Rebuild selectedFields in the exact order they were saved
                const orderedFields = savedFieldData.map((fieldData, index) => {
                    // Handle both formats: array of strings/numbers or array of objects
                    let fieldId, fieldName;
                    if (typeof fieldData === 'string' || typeof fieldData === 'number') {
                        // Simple field ID (string or number)
                        fieldId = fieldData;
                        fieldName = null; // Will be looked up from availableFields
                    } else if (typeof fieldData === 'object' && fieldData !== null) {
                        // Check if this is a weird nested structure
                        if (typeof fieldData.id === 'object' && fieldData.id !== null) {
                            if (fieldData.id.id) {
                                // Double-nested: {id: {id: "contacts.full_name", contactProperty: "full_name"}}
                                fieldId = fieldData.id.id;
                                fieldName = fieldData.id.name || fieldData.name;
                                // Preserve contactProperty and dropdownReturn from the nested object
                                fieldData = fieldData.id;
                            } else {
                                // fieldData.id is an object but has no .id property
                                console.error('Invalid field structure at index', index, ':', fieldData);
                                return null;
                            }
                        } else if (fieldData.id) {
                            // Normal object format: {id: "string", name: "string"}
                            fieldId = fieldData.id;
                            fieldName = fieldData.name;
                        } else {
                            // Object but no id property
                            console.error('Field object missing id at index', index, ':', fieldData);
                            return null;
                        }
                    } else {
                        console.error('Invalid field data type at index', index, ':', typeof fieldData, fieldData);
                        return null; // Invalid field data
                    }
                    
                    // Check if this is a blank column
                    if (typeof fieldId === 'string' && fieldId.startsWith('blank_')) {
                        // Extract name from the encoded ID
                        const parts = fieldId.split('_');
                        let name = fieldName || 'Blank Column'; // Use saved name or default
                        if (!fieldName && parts.length >= 3) {
                            // Format: blank_timestamp_encodedName
                            const encodedName = parts.slice(2).join('_');
                            try {
                                name = decodeURIComponent(encodedName);
                            } catch (e) {
                                // If decoding fails, use the encoded name as-is, replacing %20 with spaces
                                name = encodedName.replace(/%20/g, ' ').replace(/%/g, '');
                            }
                        }
                        return {
                            id: fieldId,
                            name: name,
                            isBlank: true,
                            field_id: null,
                            section_id: '[-1]'
                        };
                    }
                    
                    // Regular field lookup - use loose equality to handle string vs number IDs
                    const foundField = availableFields.find(field => field.id == fieldId);
                    if (foundField) {
                        // For contact fields with contactProperty, generate a descriptive name
                        let displayName = fieldName || foundField.name;
                        if (typeof fieldData === 'object' && fieldData.contactProperty && fieldData.contactProperty !== 'full_name') {
                            // Generate descriptive name based on contactProperty
                            const propertyLabels = {
                                'first_name': 'First Name',
                                'middle_name': 'Middle Name', 
                                'last_name': 'Last Name',
                                'company_name': 'Company Name',
                                'nickname': 'Nickname',
                                'prefix': 'Prefix',
                                'suffix': 'Suffix',
                                'salutation': 'Salutation',
                                'job_title': 'Job Title',
                                'department': 'Department',
                                'date_of_birth': 'Date of Birth',
                                'date_of_death': 'Date of Death',
                                'is_deceased': 'Deceased',
                                'is_company': 'Company',
                                'created_at': 'Created At',
                                'updated_at': 'Updated At',
                                'contact_type': 'Contact Type',
                                'gender': 'Gender',
                                'phone_number': 'Phone Number',
                                'email': 'Email',
                                'address': 'Address'
                            };
                            // Use saved name if available, otherwise construct from property
                            if (!fieldName) {
                                const baseFieldName = foundField.name.replace(' (All)', '').replace(' (First)', '');
                                displayName = propertyLabels[fieldData.contactProperty] || fieldData.contactProperty;
                                // If base name isn't generic "Client", prepend it
                                if (baseFieldName && baseFieldName !== 'Client' && !displayName.includes(baseFieldName)) {
                                    displayName = `${baseFieldName} - ${displayName}`;
                                }
                            }
                        }
                        
                        const resolvedFieldId = normalizeFieldId(fieldId);
                        return {
                            ...foundField,
                            id: resolvedFieldId, // Always use the extracted fieldId, not foundField.id
                            name: displayName, // Use constructed name for contact properties
                            // Preserve contactProperty and dropdownReturn from saved data
                            ...(typeof fieldData === 'object' && fieldData.contactProperty && { contactProperty: fieldData.contactProperty }),
                            ...(typeof fieldData === 'object' && fieldData.dropdownReturn && { dropdownReturn: fieldData.dropdownReturn })
                        };
                    }
                    // If field not found, preserve it with fallback info instead of dropping it
                    const resolvedFieldId = normalizeFieldId(fieldId);
                    console.warn(`Field ${resolvedFieldId} not found in availableFields, preserving with fallback data`);
                    return {
                        id: resolvedFieldId,
                        name: fieldName || `Field ${resolvedFieldId}`,
                        edit: 0,
                        section_id: '[-1]',
                        _notFound: true // Flag for debugging
                    };
                }); // Don't filter - preserve all fields even if not found
                
                console.log('Built orderedFields:', orderedFields);
                console.log('First field structure:', orderedFields[0]);
                console.log('First field id:', orderedFields[0]?.id, 'typeof:', typeof orderedFields[0]?.id);
                setSelectedFields(orderedFields);
            } else {
                setSelectedFields([]);
            }
            
            // Load criteria from the saved report
            if (selectedReport?.criteria) {
                const parsedCriteria = typeof selectedReport.criteria === 'string' 
                    ? JSON.parse(selectedReport.criteria) 
                    : selectedReport.criteria;
                console.log('Loading saved criteria:', parsedCriteria);
                setSelectedCriteria(parsedCriteria);
            } else {
                setSelectedCriteria({
                    logic: 'AND',
                    conditions: []
                });
            }
            
            // Set sections based on report type (only for cases and leads now, others handled above)
            if (reportType === 'tasks') {
                // Already handled in tasks branch above
                setSections([
                    { id: -1, name: 'Task Information', order_id: -1 },
                    { id: -2, name: 'Deadlines', order_id: -2 }
                ]);
            } else if (reportType === 'leads') {
                // Use customFieldsData already fetched above - don't fetch again
                const leadSections = [
                    { id: -1, name: 'Case Details', order_id: -1 },
                    { id: -3, name: 'Leads', order_id: -3 }
                ];
                if (localCustomFieldsData?.sections) {
                    leadSections.push(...localCustomFieldsData.sections);
                }
                console.log('Lead sections:', leadSections);
                setSections(leadSections);
            } else {
                // Use customFieldsData already fetched above - don't fetch again
                const caseSections = [
                    { id: -1, name: 'General', order_id: -1 }, 
                    { id: -2, name: 'Team', order_id: -2 },
                    { id: -3, name: 'Client Details', order_id: -3 }
                ];
                if (localCustomFieldsData?.sections) {
                    caseSections.push(...localCustomFieldsData.sections);
                }
                setSections(caseSections);
            }
        } catch (e) {
            console.error('Error fetching fields:', e);
        } finally {
            setLoadingCustomFields(false);
        }
    };

    const fetchReports = async (forceSelectId = null) => {
        try {
            const params = new URLSearchParams();
            if (shareToken && shareReportId) {
                params.set('id', shareReportId);
                params.set('share', shareToken);
            }
            const url = `https://api.casedb.co/reports.php${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setReports(data.reports || []);
                const targetId = forceSelectId !== null ? forceSelectId : selectedReport?.id;
                if (targetId) {
                    const updated = (data.reports || []).find(r => r.id === targetId);
                    if (updated) setSelectedReport(prev => prev && prev.id === targetId ? { ...prev, ...updated } : updated);
                }
            }
        } catch (e) {
            console.error('Error fetching reports:', e);
        }
    };

    const fetchReportValues = async (page = 1, perPage = pagination.per_page, isExport = false, sortBy = null, sortDirection = null, options = {}) => {
        const {
            signal = undefined,
            requestId = null,
            applyToState = !isExport
        } = options;
        const requestReportId = selectedReport?.id ? String(selectedReport.id) : '';

        // Collect field IDs used in criteria (including nested groups) - handle both numeric and string IDs
        const collectCriteriaFieldIds = (crit) => {
            if (!crit || !Array.isArray(crit.conditions)) return [];
            const out = [];
            for (const c of crit.conditions) {
                if (c && Array.isArray(c.conditions)) {
                    out.push(...collectCriteriaFieldIds(c));
                } else if (c && c.field_id) {
                    // For contact reports, field_id might be a string like "contacts.id"
                    // For case reports, field_id is usually numeric
                    if (!isNaN(Number(c.field_id))) {
                        out.push(Number(c.field_id));
                    } else {
                        out.push(c.field_id); // Keep as string for contact fields
                    }
                }
            }
            return out;
        };
        const criteriaFieldIds = collectCriteriaFieldIds(selectedCriteria);
        // Only include non-blank fields for the API request
        const visibleFieldIds = selectedFields.filter(Boolean).filter(f => !f.isBlank).map(f => f.id);
        // Send fields in the format backend expects: primitives for simple fields, objects for fields with properties
        const requestFields = selectedFields.filter(Boolean).filter(f => !f.isBlank).map(f => {
            // Extract the actual field ID - handle deeply nested structure from Reports.js savedFields
            // Recursively unwrap until we get to a primitive value
            let fieldId = f.id;
            while (typeof fieldId === 'object' && fieldId !== null && fieldId.id !== undefined) {
                fieldId = fieldId.id;
            }
            
            // Convert numeric IDs to strings for backend consistency
            fieldId = String(fieldId);
            
            // Check if we have additional properties (contactProperty or dropdownReturn)
            const contactProperty = f.contactProperty || (typeof f.id === 'object' && f.id?.contactProperty);
            const dropdownReturn = f.dropdownReturn || (typeof f.id === 'object' && f.id?.dropdownReturn);
            
            // If we have additional properties, send as object; otherwise send as primitive
            if (contactProperty || dropdownReturn) {
                const fieldObj = { id: fieldId };
                if (contactProperty) fieldObj.contactProperty = contactProperty;
                if (dropdownReturn) fieldObj.dropdownReturn = dropdownReturn;
                return fieldObj;
            }
            
            // Return primitive value for simple fields
            return fieldId;
        });

        const managedRequest = buildManagedPublicFormReportRequest(selectedReport, {
            page,
            perPage,
            sortBy: sortBy ?? sortConfig.key,
            sortDirection: sortDirection ?? sortConfig.direction,
        });
        const managedPublicFormReport = managedRequest !== null;
        const requestBody = managedRequest
            ? managedRequest
            : {
                fields: requestFields,
                criteria: selectedCriteria,
                page: page,
                per_page: perPage,
                sort_by: sortBy ?? sortConfig.key,
                sort_direction: sortDirection ?? sortConfig.direction,
                report_type: selectedReport?.report_type || 'cases'
            };

        // Include report ID if available for backend to determine report type
        if (!managedPublicFormReport && selectedReport?.id) {
            requestBody.report_id = selectedReport.id;
        }

        if (!managedPublicFormReport && shareToken && String(selectedReport?.id) === String(shareReportId)) {
            requestBody.share_token = shareToken;
        }

        // Add export parameter if this is an export request
        if (isExport) {
            requestBody.export = true;
        }

        let data;
        try {
            const response = await fetch('https://api.casedb.co/reports.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
                credentials: 'include',
                signal
            });
            data = await response.json();
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Report values fetch aborted');
                return null;
            }
            console.error('Error fetching report values:', error);
            if (applyToState && (!requestId || requestId === reportValuesRequestIdRef.current)) {
                setFetchingReport(false);
            }
            return null;
        }
        
        console.log('Report response:', { success: data.success, rowCount: data.data?.length || 0 });
        
        if (data.success) {
            if (managedPublicFormReport) {
                const canonicalFields = normalizePublicFormReportFields(data.fields ?? data.report?.fields);
                if (canonicalFields.length > 0) {
                    setCustomFields(canonicalFields);
                    setSelectedFields(canonicalFields);
                }
                const canonicalCriteria = parseReportCriteria(data.criteria ?? data.report?.criteria);
                if (canonicalCriteria) setSelectedCriteria(canonicalCriteria);
                if (data.report && typeof data.report === 'object') {
                    setSelectedReport((current) => current ? { ...current, ...data.report } : current);
                }
            }
            const isLatestRequest = !requestId || requestId === reportValuesRequestIdRef.current;
            const isCurrentReport = !requestReportId || requestReportId === String(selectedReportIdRef.current || '');

            if (!applyToState || !isLatestRequest || !isCurrentReport) {
                return data.data;
            }

            setReportData(data.data);
            setLookupMaps(data.lookupMaps || {});
            if (data.pagination) {
                console.log('Received pagination:', data.pagination);
                setPagination(data.pagination);
            }
            setFetchingReport(false);
            return data.data;
        } else {
            console.error('Error fetching report values:', data.message);
            if (applyToState && (!requestId || requestId === reportValuesRequestIdRef.current)) {
                setFetchingReport(false);
            }
        }
    };

    const runReportValuesFetch = (page = 1, perPage = pagination.per_page, sortBy = null, sortDirection = null) => {
        if (reportValuesAbortRef.current) {
            reportValuesAbortRef.current.abort();
        }

        const controller = new AbortController();
        const requestId = reportValuesRequestIdRef.current + 1;
        reportValuesAbortRef.current = controller;
        reportValuesRequestIdRef.current = requestId;
        setReportData(null);
        setLookupMaps({});
        setPagination((current) => ({
            ...current,
            current_page: page,
            per_page: perPage,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false
        }));
        setFetchingReport(true);

        return fetchReportValues(page, perPage, false, sortBy, sortDirection, {
            signal: controller.signal,
            requestId,
            applyToState: true
        }).finally(() => {
            if (reportValuesRequestIdRef.current === requestId && reportValuesAbortRef.current === controller) {
                reportValuesAbortRef.current = null;
            }
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            runReportValuesFetch(newPage, pagination.per_page);
        }
    };

    const handlePerPageChange = (newPerPage) => {
        runReportValuesFetch(1, newPerPage);
    };

    const handleSort = (key) => {
        const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction: newDirection });
        // Pass sort config directly since state update is async
        // Parameters: (page, perPage, isExport, sortBy, sortDirection)
        runReportValuesFetch(pagination.current_page || 1, pagination.per_page, key, newDirection);
    };

    const downloadExcel = async () => {
        // Use the same field collection logic as fetchReportValues
        const collectCriteriaFieldIds = (crit) => {
            if (!crit || !Array.isArray(crit.conditions)) return [];
            const out = [];
            for (const c of crit.conditions) {
                if (c && Array.isArray(c.conditions)) {
                    out.push(...collectCriteriaFieldIds(c));
                } else if (c && c.field_id) {
                    // For contact reports, field_id might be a string like "contacts.id"
                    // For case reports, field_id is usually numeric
                    if (!isNaN(Number(c.field_id))) {
                        out.push(Number(c.field_id));
                    } else {
                        out.push(c.field_id); // Keep as string for contact fields
                    }
                }
            }
            return out;
        };
        const criteriaFieldIds = collectCriteriaFieldIds(selectedCriteria);
        // Only include non-blank fields for the API request
        const requestFields = selectedFields
            .filter(Boolean)
            .filter(f => !f.isBlank)
            .map(f => ({
                id: normalizeFieldId(f.id),
                ...(f.contactProperty && { contactProperty: f.contactProperty }),
                ...(f.dropdownReturn && { dropdownReturn: f.dropdownReturn })
            }));

        const managedRequest = buildManagedPublicFormReportRequest(selectedReport, {
            page: 1,
            perPage: 999999,
            sortBy: sortConfig.key,
            sortDirection: sortConfig.direction,
            export: true,
        });
        const managedPublicFormReport = managedRequest !== null;
        const requestBody = managedRequest
            ? managedRequest
            : {
                fields: requestFields,
                criteria: selectedCriteria,
                page: 1,
                per_page: 999999, // Fetch all results for export
                sort_by: sortConfig.key,
                sort_direction: sortConfig.direction,
                report_type: selectedReport?.report_type || 'cases',
                export: true
            };

        // Include report ID if available for backend to determine report type
        if (!managedPublicFormReport && selectedReport?.id) {
            requestBody.report_id = selectedReport.id;
        }

        if (!managedPublicFormReport && shareToken && String(selectedReport?.id) === String(shareReportId)) {
            requestBody.share_token = shareToken;
        }

        const response = await fetch('https://api.casedb.co/reports.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            credentials: 'include'
        });
        const fullResponse = await response.json();
        
        if (!fullResponse.success || !Array.isArray(selectedFields) || !Array.isArray(fullResponse.data)) {
            alert('Missing report data.');
            return;
        }
        
        const dataResponse = fullResponse.data;
        
        // Get the lookup maps from the full response
        const lookupMaps = fullResponse.lookupMaps || {};
        const customFieldTypes = lookupMaps.customFieldTypes || {};
        
        const headerOrder = selectedFields.filter(Boolean).map(getOutputKey);
        // For header names, preserve the field's actual name from selectedFields
        // Don't look up from customFields which might have a generic name like "Client"
        const headerNames = selectedFields.filter(Boolean).map(f => {
            const name = f.name
                || (typeof f.id === 'object' && f.id !== null ? f.id.name : null)
                || normalizeFieldId(f.id);
            return neutralizeSpreadsheetCell(name);
        });
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Report');
        
        // Create header row
        const headerRow = ws.addRow(headerNames);
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4F81BD' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });
        
        // Add data rows
        dataResponse.forEach(r => {
            const vals = headerOrder.map(k => {
                const field = selectedFields.find(f => getOutputKey(f) === k);
                // For blank columns, return empty string
                if (field?.isBlank) {
                    return '';
                }
                
                // Get field type to determine if we should use raw or formatted data
                const fieldId = String(k);
                let customFieldId = null;
                
                // Extract custom field ID for type checking
                if (fieldId.match(/^\d+$/)) {
                    customFieldId = parseInt(fieldId);
                } else if (fieldId.startsWith('custom_fields_')) {
                    customFieldId = parseInt(fieldId.replace('custom_fields_', ''));
                } else if (fieldId.includes('_')) {
                    const parts = fieldId.split('_');
                    const lastPart = parts[parts.length - 1];
                    if (lastPart.match(/^\d+$/)) {
                        customFieldId = parseInt(lastPart);
                    }
                }
                
                const fieldTypeId = customFieldId ? (
                    customFieldTypes[customFieldId] || 
                    customFieldTypes[String(customFieldId)] ||
                    customFieldTypes[parseInt(customFieldId)]
                ) : null;
                
                const rawData = r._rawData || {};
                const rawValue = rawData[k];
                const formattedValue = r[k];
                
                // For certain field types, always use formatted value instead of raw value
                // Type 8: Contact fields - use formatted value (contact name)
                // Type 10: Dropdown fields - use formatted value (option text, not index)
                // Team fields: use formatted value (user name)
                // Lookup fields: use formatted value (mapped name, not raw ID)
                let value;
                
                // CRITICAL: For money/number fields (types 3, 4, 5, 17) and computed currency fields,
                // ALWAYS use raw value if available to ensure proper Excel formatting
                if ((customFieldId && (fieldTypeId === 3 || fieldTypeId === 4 || fieldTypeId === 5 || fieldTypeId === 17)) ||
                    fieldId.startsWith('highest_') || fieldId.startsWith('total_section_')) {
                    // Money, percent, number, total_data_ident, and computed currency fields
                    value = (rawValue !== undefined && rawValue !== null) ? rawValue : formattedValue;
                }
                else if (fieldTypeId === 8 || fieldTypeId === 10 || fieldTypeId === 12 ||
                    fieldId.includes('contact') || fieldId.includes('referral') ||
                    fieldId === 'leads.db_origination' || fieldId === 'leads.referral_contact' ||
                    fieldId === 'leads.intake_by' || fieldId === 'leads.assigned_to' ||
                    fieldId === 'leads.assigned_to_attorney' || fieldId === 'leads.referred_to' ||
                    fieldId === 'leads.case_type_id' || fieldId === 'leads.status' ||
                    fieldId === 'leads.office' || fieldId === 'leads.marketing_source' ||
                    fieldId === 'leads.marketing_lead' ||
                    fieldId === 'cases.template_id' || fieldId === 'cases.phase_id' ||
                    fieldId === 'cases.subphase_id' || fieldId === 'cases.contact_id' ||
                    fieldId === 'cases.created_by' ||
                    fieldId.match(/^cases\.([a-zA-Z_]+)\[['\"]?\d+['\"]?\]\[\d+\]$/)) {
                    // Contact fields (type 8), dropdown fields (type 10), boolean fields (type 12),
                    // lookup fields, and team fields - ALWAYS use formatted value
                    // These should display human-readable text, not raw IDs or numbers 
                    // BUT if it's a contact field and formatted value is an object, extract the property
                    // Check both fieldTypeId and field.field_id to catch all contact field types
                    if ((fieldTypeId === 8 || field?.field_id === 8) && typeof formattedValue === 'object' && formattedValue !== null && !Array.isArray(formattedValue)) {
                        // Contact field returned as object - extract the selected property
                        const contactProperty = field?.contactProperty || 'full_name';
                        value = formattedValue[contactProperty] || formattedValue.full_name || '';
                    } else {
                        value = formattedValue;
                    }
                } else if (rawValue !== undefined && rawValue !== null) {
                    // Other fields - use raw value for proper formatting
                    value = rawValue;
                } else {
                    // Fallback to formatted value
                    value = formattedValue;
                }

                const formattedDeadline = formatDeadlineExportValue(value);
                if (formattedDeadline !== null) {
                    return formattedDeadline;
                }

                const formattedMultiValue = formatMultiValueExportValue(value);
                if (formattedMultiValue !== null) {
                    return formattedMultiValue;
                }
                
                // Handle arrays and null values
                if (Array.isArray(value)) {
                    return value.map((item) => {
                        if (item === null || item === undefined || item === '') return '';
                        if (typeof item !== 'object') return String(item);
                        return String(item.file_name ?? item.name ?? item.label ?? item.display ?? item.value ?? 'Attached file');
                    }).filter(Boolean).join(', ');
                }

                if (value && typeof value === 'object') {
                    return JSON.stringify(value);
                }

                return value ?? '';
            });
            
            const safeVals = vals.map((value) => (
                typeof value === 'string' ? neutralizeSpreadsheetCell(value) : value
            ));
            const addedRow = ws.addRow(safeVals);
            
            // Apply proper Excel formatting to each cell based on field type
            safeVals.forEach((cellValue, colIndex) => {
                const fieldId = String(headerOrder[colIndex]);
                const cell = addedRow.getCell(colIndex + 1);
                if (typeof cellValue === 'string' && cellValue.includes('\n')) {
                    cell.alignment = { ...(cell.alignment || {}), wrapText: true, vertical: 'top' };
                }
                
                // Get field type from backend data - handle different field ID formats
                let customFieldId = null;
                
                // Check if this is a custom field (not a standard leads table field)
                if (typeof fieldId === 'string') {
                    // Extract custom field ID from different possible formats
                    if (fieldId.match(/^\d+$/)) {
                        // Pure number string like "123"
                        customFieldId = parseInt(fieldId);
                    } else if (fieldId.startsWith('custom_fields_')) {
                        // Format like "custom_fields_123"
                        customFieldId = parseInt(fieldId.replace('custom_fields_', ''));
                    } else if (fieldId.includes('_')) {
                        // Other underscore format - try to extract number from end
                        const parts = fieldId.split('_');
                        const lastPart = parts[parts.length - 1];
                        if (lastPart.match(/^\d+$/)) {
                            customFieldId = parseInt(lastPart);
                        }
                    }
                } else if (typeof fieldId === 'number') {
                    customFieldId = fieldId;
                }
                
                const fieldTypeId = customFieldId ? (
                    customFieldTypes[customFieldId] || 
                    customFieldTypes[String(customFieldId)] ||
                    customFieldTypes[parseInt(customFieldId)]
                ) : null;
                
                // Always set cell value first
                cell.value = cellValue;

                // Public-form answers are untrusted recipient input. Keep every
                // string as the already-neutralized literal and do not run the
                // ordinary report coercions that can turn it into a formula.
                if (managedPublicFormReport) return;
                
                // Apply Excel formatting based on field type ID from fields table
                if (customFieldId && (fieldTypeId === 3 || fieldTypeId === 17)) {
                    // money (3) or total_data_ident (17) → Currency format
                    if (typeof cellValue === 'number') {
                        cell.numFmt = '"$"#,##0.00';
                    } else if (typeof cellValue === 'string' && cellValue !== '') {
                        const numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                        if (!isNaN(numValue)) {
                            cell.value = numValue;
                            cell.numFmt = '"$"#,##0.00';
                        }
                    }
                } else if (fieldId.startsWith('highest_') || fieldId.startsWith('total_section_')) {
                    // Computed currency fields - highest_ and total_section_ 
                    if (typeof cellValue === 'number') {
                        cell.numFmt = '"$"#,##0.00';
                    } else if (typeof cellValue === 'string' && cellValue !== '') {
                        const numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                        if (!isNaN(numValue)) {
                            cell.value = numValue;
                            cell.numFmt = '"$"#,##0.00';
                        }
                    }
                } else if (customFieldId && fieldTypeId === 4) {
                    // percent → Percentage format
                    // Convert from percentage (1 = 1%) to decimal (0.01) for Excel
                    if (typeof cellValue === 'number') {
                        let percentValue = cellValue / 100; // Always convert from percentage to decimal
                        cell.value = percentValue;
                        cell.numFmt = '0.00%';
                    } else if (typeof cellValue === 'string' && cellValue !== '') {
                        let numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                        if (!isNaN(numValue)) {
                            let percentValue = numValue / 100; // Always convert from percentage to decimal
                            cell.value = percentValue;
                            cell.numFmt = '0.00%';
                        }
                    }
                } else if (customFieldId && fieldTypeId === 5) {
                    // number → Number format
                    if (typeof cellValue === 'number') {
                        cell.numFmt = '#,##0.00';
                    } else if (typeof cellValue === 'string' && cellValue !== '') {
                        const numValue = parseFloat(cellValue);
                        if (!isNaN(numValue)) {
                            cell.value = numValue;
                            cell.numFmt = '#,##0.00';
                        }
                    }
                } else if (customFieldId && fieldTypeId === 6) {
                    // date → Date format
                    if (typeof cellValue === 'string' && cellValue !== '') {
                        const date = new Date(cellValue);
                        if (!isNaN(date.getTime())) {
                            cell.value = date;
                            cell.numFmt = 'mm/dd/yyyy';
                        }
                    }
                } else if (customFieldId && fieldTypeId === 7) {
                    // boolean → True/False text (0 = true, 1 = false)
                    if (cellValue === 0 || cellValue === '0') {
                        cell.value = 'True';
                    } else if (cellValue === 1 || cellValue === '1') {
                        cell.value = 'False';
                    }
                }
                
                // Handle built-in date fields (non-custom fields) - these have string IDs like 'cases.create_date'
                if (fieldId.includes('date') || fieldId.includes('Date') || 
                    fieldId === 'cases.create_date' || fieldId === 'cases.updated_at' ||
                    fieldId === 'leads.created_at' || fieldId === 'leads.updated_at' ||
                    fieldId === 'leads.status_date') {
                    if (typeof cellValue === 'string' && cellValue !== '') {
                        const date = new Date(cellValue);
                        if (!isNaN(date.getTime())) {
                            cell.value = date;
                            cell.numFmt = 'mm/dd/yyyy';
                        }
                    }
                }
            });
            
            // Add hyperlinks for case IDs and names
            const cid = managedPublicFormReport ? null : r['cases.id'];
            if (cid) {
                const url = `https://casedb.co/case/${cid}`;
                const idIdx = headerOrder.indexOf('cases.id');
                if (idIdx !== -1) {
                    const cell = addedRow.getCell(idIdx + 1);
                    cell.value = { text: String(cid), hyperlink: url };
                    cell.font = { color: { argb: 'FF0000FF' }, underline: true };
                }
                const nameIdx = headerOrder.indexOf('cases.case_name');
                if (nameIdx !== -1) {
                    const cell = addedRow.getCell(nameIdx + 1);
                    const caseName = r['cases.case_name'] || '';
                    if (caseName) {
                        cell.value = { text: caseName, hyperlink: url };
                        cell.font = { color: { argb: 'FF0000FF' }, underline: true };
                    }
                }
            }
        });
        
        // Auto-size columns based on both header text and data content
        ws.columns.forEach((col, index) => {
            let max = 10;
            
            // Include header text length in calculation
            const headerText = headerNames[index] || '';
            const headerLength = headerText.length;
            if (headerLength > max) max = headerLength;
            
            // Check data cell lengths with smart limits for different data types
            col.eachCell({ includeEmpty: true }, c => {
                if (c.value) {
                    let len;
                    if (c.value.text) {
                        len = c.value.text.length;
                    } else {
                        const strValue = String(c.value);
                        
                        // Apply reasonable limits for common data types
                        if (strValue.match(/^\w+\s+\d{1,2},\s+\d{4}/)) {
                            // Date format like "October 23, 2025" - limit to reasonable date width
                            len = Math.min(strValue.length, 18);
                        } else if (strValue.match(/^\$[\d,]+(\.\d{2})?$/)) {
                            // Currency format - limit to reasonable currency width
                            len = Math.min(strValue.length, 15);
                        } else if (strValue.length > 50) {
                            // Very long text - limit to prevent extremely wide columns
                            len = Math.min(strValue.length, 50);
                        } else {
                            len = strValue.length;
                        }
                    }
                    if (len > max) max = len;
                }
            });
            
            // Set column width with reasonable bounds
            col.width = Math.min(60, Math.max(headerLength + 2, max + 2));
        });
        
        // Calculate and set header row height ONLY if text needs to wrap
        let maxHeaderLines = 1;
        let needsWrap = false;
        
        headerRow.eachCell((cell, colNumber) => {
            const headerText = String(cell.value || '');
            const columnWidth = ws.getColumn(colNumber).width || 10;
            
            // Estimate characters that fit per line based on column width
            // Excel column width units are roughly equivalent to character count for standard fonts
            const charsPerLine = Math.floor(columnWidth * 0.85); // 0.85 factor for padding and margins
            
            // Only calculate lines if text is actually longer than what fits in one line
            if (headerText.length > charsPerLine) {
                needsWrap = true;
                const estimatedLines = Math.ceil(headerText.length / charsPerLine);
                if (estimatedLines > maxHeaderLines) {
                    maxHeaderLines = estimatedLines;
                }
            }
        });
        
        // Only set custom header row height if text actually needs to wrap
        if (needsWrap) {
            const baseHeight = 20;
            const lineHeight = 15;
            headerRow.height = baseHeight + (maxHeaderLines - 1) * lineHeight;
        }
        // If no wrapping needed, let Excel use default height
        
        const fname = `${selectedReport?.name?.replace(/[\\/:*?"<>|]/g, '') || 'Report'}.xlsx`;
        const buf = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fname);
        setMessage({ text: 'Report exported successfully!', type: 'success' });
    };

    const saveReport = async (reportName, save_type) => {
        if (isPublicFormReportDefinition(selectedReport)) {
            setMessage({ text: 'Form reports are managed from their public form.', type: 'info' });
            return;
        }
        // Persist full field config so contactProperty/dropdownReturn survive reloads
        const fieldsToSave = selectedFields.filter(Boolean).map(field => {
            const baseId = (typeof field.id === 'object' && field.id !== null && field.id.id)
                ? field.id.id
                : field.id;
            const payload = { id: baseId };
            if (field.name) payload.name = field.name;
            if (field.contactProperty) payload.contactProperty = field.contactProperty;
            if (field.dropdownReturn) payload.dropdownReturn = field.dropdownReturn;
            if (field.isBlank) payload.isBlank = true;
            // Preserve type hints if present
            if (field.field_id !== undefined) payload.field_id = field.field_id;
            if (field.section_id !== undefined) payload.section_id = field.section_id;
            return payload;
        });
        
        console.log('Saving fields:', fieldsToSave);
        
        const response = await fetch('https://api.casedb.co/report-update.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                action: save_type,
                fields: fieldsToSave,
                criteria: selectedCriteria,
                report_name: reportName,
                report_id: selectedReport?.id || null,
                report_type: selectedReport?.report_type || 'cases', // Preserve report type
                ...(shareToken && String(selectedReport?.id) === String(shareReportId)
                    ? { share_token: shareToken } : {}),
            })
        });
        const data = await response.json();
        if (data.success) {
            setMessage({ text: data.message || 'Report saved successfully!', type: 'success' });
            let newId = selectedReport?.id;
            if (data.report) {
                newId = data.report.id;
                setSelectedReport(data.report); // optimistic set
                setSelectedStep(4);
            }
            fetchReports(newId); // ensure persistence
        } else {
            setMessage({ text: data.message || 'Failed to save report', type: 'error' });
        }
    }

    const openRenameReportModal = () => {
        if (!selectedReport) return;

        if (isPublicFormReportDefinition(selectedReport)) {
            openPublicForms(selectedReport.public_form_id);
            return;
        }

        if (Number(selectedReport.premade) === 1) {
            setMessage({ text: 'Premade reports must be saved to My Reports before renaming.', type: 'info' });
            setSelectedStep(4);
            return;
        }

        setRenameReportName(selectedReport.name || '');
        setShowRenameReport(true);
    };

    const commitRenameReport = () => {
        const trimmedName = renameReportName.trim();
        if (!trimmedName) {
            setMessage({ text: 'Report name is required.', type: 'error' });
            return;
        }

        saveReport(trimmedName, 'save');
        setShowRenameReport(false);
    };

    const deleteReport = async (report) => {
        if (isPublicFormReportDefinition(report)) {
            setMessage({ text: 'Form reports follow their public form and cannot be deleted separately.', type: 'info' });
            return;
        }
        const reportId = Number(report?.id);
        if (!Number.isFinite(reportId) || reportId <= 0) return;

        try {
            const response = await fetch('https://api.casedb.co/report-update.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'archive', report_id: reportId }),
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                const deletedActiveReport = Number(selectedReport?.id) === reportId;
                setReports((currentReports) => currentReports.filter((item) => Number(item.id) !== reportId));

                if (deletedActiveReport) {
                    if (hubFetchTimeoutRef.current) {
                        clearTimeout(hubFetchTimeoutRef.current);
                        hubFetchTimeoutRef.current = null;
                    }
                    if (reportValuesAbortRef.current) {
                        reportValuesAbortRef.current.abort();
                    }
                    reportValuesRequestIdRef.current += 1;
                    lastHubFetchKeyRef.current = '';
                    setSelectedReport(null);
                }
            }

            setMessage({ text: data.message || (data.success ? 'Report deleted' : 'Failed to delete report'), type: data.success ? 'success' : 'error' });
        } catch (error) {
            console.error('Error deleting report:', error);
            setMessage({ text: 'Failed to delete report', type: 'error' });
        }
    };

    const selectReportForHub = (report) => {
        if (hubFetchTimeoutRef.current) {
            clearTimeout(hubFetchTimeoutRef.current);
            hubFetchTimeoutRef.current = null;
        }
        if (reportValuesAbortRef.current) {
            reportValuesAbortRef.current.abort();
        }
        reportValuesRequestIdRef.current += 1;
        lastHubFetchKeyRef.current = '';
        setReportMode('hub');
        setReportData(null);
        setSelectedFields([]);
        setSelectedCriteria({ logic: 'AND', conditions: [] });
        setPagination((current) => ({ ...current, current_page: 1 }));
        setSortConfig({ key: null, direction: 'asc' });
        setFetchingReport(Boolean(report));
        setSelectedReport(report);
    };

    const closeReportHubPreview = () => {
        selectReportForHub(null);
    };

    const openReportBuilder = (step = 1) => {
        if (isPublicFormReport) {
            openPublicForms(selectedReport?.public_form_id);
            return;
        }
        setReportMode('builder');
        setSelectedStep(step);
    };

    const openPublicForms = (formId = null, view = '') => {
        const params = new URLSearchParams();
        const numericFormId = Number(formId);
        if (Number.isInteger(numericFormId) && numericFormId > 0) {
            params.set('form_id', String(numericFormId));
            if (view === 'submissions') params.set('view', 'submissions');
        }
        navigate(`${PUBLIC_FORMS_PATH}${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const createReports = () => {
        setReportMode('builder');
        setSelectedReport(null);
        setSelectedStep(0);
    };

    const createListReport = () => {
        const normalizeName = (value) => String(value || '').trim().toLowerCase();
        const caseListTemplate = reports.find((report) =>
            Number(report.premade) === 1 && ['case list', 'cases list'].includes(normalizeName(report.name))
        ) || reports.find((report) =>
            Number(report.premade) === 1 && normalizeName(report.name).includes('case') && normalizeName(report.name).includes('list')
        );
        const fallbackFields = [
            { id: 'cases.id', name: 'Case ID', edit: 0, section_id: '[-1]' },
            { id: 'cases.case_name', name: 'Case Name', edit: 1, section_id: '[-1]' },
            { id: 'cases.contact_id', name: 'Client', field_id: 8, edit: 1, section_id: '[-1]' },
            { id: 'cases.phase_id', name: 'Case Phase', edit: 1, section_id: '[-1]' }
        ];
        let templateFields = caseListTemplate?.fields || fallbackFields;

        try {
            const parsedFields = typeof templateFields === 'string' ? JSON.parse(templateFields) : templateFields;
            if (!Array.isArray(parsedFields) || parsedFields.length === 0) {
                templateFields = fallbackFields;
            }
        } catch (error) {
            templateFields = fallbackFields;
        }

        const listCriteria = {
            logic: 'AND',
            report_mode: 'list',
            ordered_case_ids: [],
            selected_cases: [],
            conditions: [{
                field_id: 'cases.id',
                operator: '=',
                value: [],
                _list_report: true
            }]
        };
        const listReport = {
            id: null,
            name: 'New List Report',
            fields: typeof templateFields === 'string' ? templateFields : JSON.stringify(templateFields),
            criteria: JSON.stringify(listCriteria),
            premade: 1,
            creator: '',
            report_type: 'cases',
            _reportMode: 'list'
        };

        setReportMode('builder');
        setReportData(null);
        setSelectedFields([]);
        setSelectedCriteria(listCriteria);
        setSortConfig({ key: null, direction: 'asc' });
        setSelectedReport(listReport);
        setSelectedStep(1);
    };

    const openReportSaveAction = (action) => {
        if (!selectedReport || isPublicFormReport) return;
        setReportSaveInitialAction(action);
    };

    const renderReportHubActions = () => {
        const hasReport = !!selectedReport;
        const reportReady = hasReport && selectedFields.length > 0;
        const isPremade = Number(selectedReport?.premade) === 1;
        const disableOwnedReportActions = !hasReport || isPremade;

        return (
            <div className='report-hub-actions' aria-label='Report actions'>
                <button type='button' className='report-hub-action' onClick={downloadCSV} disabled={!reportReady} title='Download CSV' aria-label='Download CSV'>
                    <FileText size={16} />
                </button>
                <button type='button' className='report-hub-action' onClick={downloadExcel} disabled={!reportReady} title='Download Excel' aria-label='Download Excel'>
                    <FileDigit size={16} />
                </button>
                <button type='button' className='report-hub-action' onClick={downloadWord} disabled={!reportReady} title='Download Word' aria-label='Download Word'>
                    <FileType2 size={16} />
                </button>
                {isPublicFormReport ? (
                    <>
                        <button type='button' className='report-hub-action' onClick={() => openPublicForms(selectedReport.public_form_id)} title='Manage form' aria-label='Manage form'>
                            <SquarePen size={16} />
                        </button>
                        <button type='button' className='report-hub-action' onClick={() => openPublicForms(selectedReport.public_form_id, 'submissions')} title='View submissions' aria-label='View submissions'>
                            <Users size={16} />
                        </button>
                    </>
                ) : (
                    <>
                        <button type='button' className='report-hub-action' onClick={() => openReportSaveAction('share')} disabled={disableOwnedReportActions} title='Share report' aria-label='Share report'>
                            <Share size={16} />
                        </button>
                        <button type='button' className='report-hub-action' onClick={() => openReportBuilder(1)} disabled={!hasReport} title='Edit report' aria-label='Edit report'>
                            <SquarePen size={16} />
                        </button>
                        <button type='button' className='report-hub-action' onClick={() => openReportSaveAction('schedule')} disabled={disableOwnedReportActions} title='Auto email settings' aria-label='Auto email settings'>
                            <Clock size={16} />
                        </button>
                        <button type='button' className='report-hub-action danger' onClick={() => deleteReport(selectedReport)} disabled={disableOwnedReportActions} title='Archive report' aria-label='Archive report'>
                            <Archive size={16} />
                        </button>
                    </>
                )}
                {canAccessPublicForms(accessLevel) && (
                    <button
                        type='button'
                        className='report-hub-action report-hub-form-launch'
                        onClick={() => openPublicForms()}
                        title='Build or manage public forms'
                        aria-label='Build public form'
                    >
                        <ClipboardList size={16} />
                        <span>Public forms</span>
                    </button>
                )}
            </div>
        );
    };


    const downloadWord = async () => {
        const dataResponse = await fetchReportValues(1, 999999, true); // Fetch all results for export
        if (!Array.isArray(selectedFields) || !Array.isArray(dataResponse)) {
            alert("Missing report data.");
            return;
        }

        const paragraphs = [];

        const reportTitle = selectedReport?.name || "Untitled Report";

        paragraphs.push(new Paragraph({
            children: [
                new TextRun({
                    text: reportTitle,
                    bold: true,
                    size: 28,
                })
            ],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
        }));

        dataResponse.forEach(row => {
            selectedFields.forEach(field => {
                const key = getOutputKey(field);
                let val = getExportDisplayValue(row, field, key);

                if (val === '') val = '';

                paragraphs.push(new Paragraph({
                    children: [
                        new TextRun({ text: `${field.name}: `, bold: true }),
                        new TextRun({ text: `${val}` })
                    ],
                    spacing: { after: 120 }
                }));
            });

            paragraphs.push(new Paragraph({ text: "", spacing: { after: 200 } }));
        });

        const doc = new Document({
            sections: [{
                properties: {},
                children: paragraphs
            }]
        });

        const blob = await Packer.toBlob(doc);
        saveAs(blob, `${selectedReport?.name?.replace(/[\\/:*?"<>|]/g, '') || 'Report'}.docx`);
        setMessage({ text: 'Report exported successfully!', type: 'success' });
    };

    const downloadCSV = async () => {
        const dataResponse = await fetchReportValues(1, 999999, true); // Fetch all results for export
        if (!Array.isArray(selectedFields) || !Array.isArray(dataResponse)) {
            alert("Missing report data.");
            return;
        }

        const headerOrder = selectedFields.map(getOutputKey);
        const headerNames = selectedFields.map(field => {
            const name = field?.name
                || (typeof field?.id === 'object' && field?.id !== null ? field.id.name : null)
                || normalizeFieldId(field?.id);
            return quoteCsvCell(name);
        });

        const rows = dataResponse.map(row => {
            return headerOrder.map(key => {
                // For blank columns, return empty string
                const sf = selectedFields.find(f => getOutputKey(f) === key);
                const value = getExportDisplayValue(row, sf, key);
                return quoteCsvCell(value);
            }).join(',');
        });

        const csvContent = [headerNames.join(','), ...rows].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        const filename = `${selectedReport?.name?.replace(/[\\/:*?"<>|]/g, '') || 'Report'}.csv`;
        saveAs(blob, filename);
        setMessage({ text: 'Report exported successfully!', type: 'success' });
    };

    const handleAIReportGenerate = async (prompt) => {
        try {
            setMessage(null);
            const response = await fetch('https://api.casedb.co/ai_report_generator.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt }),
                credentials: 'include'
            });
            
            const result = await response.json();
            
            if (result.success && result.config) {
                const config = result.config;
                
                // Create a new report object
                const aiReport = {
                    id: null,
                    name: config.name,
                    fields: config.fields,
                    criteria: config.criteria,
                    premade: 0,
                    creator: 'AI Generated'
                };
                
                // Set the AI-generated report as selected
                setSelectedReport(aiReport);
                
                // For now, just set the field IDs - the fetchFields useEffect will map them to full objects
                // Check if fields are already objects (new format) or strings (old format)
                setSelectedFields(config.fields.map(fieldId => 
                    typeof fieldId === 'object' ? fieldId : { id: fieldId }
                ));
                setSelectedCriteria(config.criteria);
                
                // Move to column setup step
                setSelectedStep(1);
                
                setMessage({
                    type: 'success',
                    text: `AI generated report "${config.name}" successfully! Review and adjust the columns and criteria as needed.`
                });
            } else {
                setMessage({
                    type: 'error',
                    text: result.message || 'Failed to generate report with AI'
                });
            }
        } catch (error) {
            console.error('AI report generation error:', error);
            setMessage({
                type: 'error',
                text: 'An error occurred while generating the report'
            });
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    // Check for URL parameter ?id= and auto-load report
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const reportId = urlParams.get('id');
        
        if (reportId && reports.length > 0 && !selectedReport) {
            // Find the report by ID - if it's in the reports array, user has access
            const report = reports.find(r => String(r.id) === String(reportId));
            
            if (report) {
                console.log('Auto-loading report from URL:', report.name);
                setSelectedReport(report);
                // Don't jump to step 3 yet - let the next useEffect handle it after data loads
            } else {
                console.warn('Report ID from URL not found or not accessible:', reportId);
            }
        }
    }, [reports, selectedReport]);

    useEffect(() => {
        const selectedReportId = selectedReport?.id ? String(selectedReport.id) : null;
        if (selectedReportIdRef.current !== selectedReportId) {
            if (hubFetchTimeoutRef.current) {
                clearTimeout(hubFetchTimeoutRef.current);
                hubFetchTimeoutRef.current = null;
            }
            if (reportValuesAbortRef.current) {
                reportValuesAbortRef.current.abort();
            }
            reportValuesRequestIdRef.current += 1;
            lastHubFetchKeyRef.current = '';
        }

        if (!selectedReport) {
            // Clear all leftover data when no report is selected
            if (reportMode === 'builder') {
                setSelectedStep(0);
            }
            setSelectedFields([]);
            setSelectedCriteria({ logic: 'AND', conditions: [] });
            setReportData(null);
            setPagination({
                current_page: 1,
                per_page: 100,
                total: 0,
                total_pages: 0,
                has_next: false,
                has_prev: false
            });
            setSortConfig({ key: null, direction: 'asc' });
            return;
        } else if (selectedReport && reportMode === 'hub') {
            setFetchingReport(true);
        } else if (selectedReport && selectedStep === 0 && reportMode === 'builder') {
            setSelectedStep(1);
        }

        // Re-fetch fields when report type changes
        fetchFields();
    }, [selectedReport, reportMode]);

    useEffect(() => {
        // Parse and set the selected fields from the report configuration
        // Only run this for 'cases' report type - other types are handled by fetchFields()
        const reportType = selectedReport?.report_type || 'cases';
        
        // SKIP completely for tasks and contacts reports
        // fetchFields() already handles ALL field loading and selection for these types
        if (reportType === 'tasks' || reportType === 'contacts' || reportType === 'leads' || isPublicFormReportDefinition(selectedReport)) {
            return;
        }
        
        // SKIP if we don't have customFields loaded yet
        if (!customFields || customFields.length === 0) {
            return;
        }
        
        // SKIP if there are no fields to restore from the report
        if (!selectedReport?.fields) {
            return;
        }
        
        // Only for 'cases' report type: map saved field IDs to full field objects
        if (selectedReport?.fields && customFields && customFields.length > 0) {
            const savedFieldData = typeof selectedReport.fields === 'string' 
                ? JSON.parse(selectedReport.fields) 
                : selectedReport.fields;

            // Newer reports persist full field objects; fetchFields already restores them.
            if (Array.isArray(savedFieldData) && savedFieldData.some(fieldData => typeof fieldData === 'object' && fieldData !== null)) {
                return;
            }
            
            console.log('=== FIELD MAPPING DEBUG ===');
            console.log('Saved field IDs:', savedFieldData);
            console.log('CustomFields count:', customFields.length);
            console.log('CustomFields sample IDs:', customFields.slice(0, 10).map(f => ({ id: f.id, type: typeof f.id, name: f.name })));
            
            // Map saved field IDs to field objects from customFields
            const orderedFields = savedFieldData.map(fieldId => {
                // Handle blank columns
                if (typeof fieldId === 'string' && fieldId.startsWith('blank_')) {
                    const parts = fieldId.split('_');
                    let name = 'Blank Column';
                    if (parts.length >= 3) {
                        const encodedName = parts.slice(2).join('_');
                        try {
                            name = decodeURIComponent(encodedName);
                        } catch (e) {
                            name = encodedName.replace(/%20/g, ' ').replace(/%/g, '');
                        }
                    }
                    return {
                        id: fieldId,
                        name: name,
                        isBlank: true,
                        field_id: null,
                        section_id: '[-1]'
                    };
                }
                
                // Find the field in customFields using loose equality (handles string vs number)
                if (customFields && Array.isArray(customFields)) {
                    // Try both direct comparison and string/number conversion
                    const foundField = customFields.find(f => {
                        // Direct comparison (handles string to string, number to number)
                        if (f.id == fieldId) return true;
                        // Compare as strings (handles "53" === "53")
                        if (String(f.id) === String(fieldId)) return true;
                        // Compare as numbers (handles 53 === 53)
                        if (Number(f.id) === Number(fieldId)) return true;
                        return false;
                    });
                    if (foundField) {
                        console.log(`✓ Matched field ${fieldId} (${typeof fieldId}) to:`, foundField.name);
                        return foundField;
                    }
                }
                
                // If field not found yet, create a placeholder that will be resolved when customFields loads
                const resolvedFieldId = normalizeFieldId(fieldId);
                console.warn(`✗ Field ${resolvedFieldId} not found in customFields - fieldId type: ${typeof fieldId}`);
                return {
                    id: resolvedFieldId,
                    name: `Field ${resolvedFieldId}`,
                    edit: 0,
                    section_id: '[-1]'
                };
            }).filter(Boolean);
            
            console.log('Ordered fields result:', orderedFields.map(f => ({ id: f.id, name: f.name })));
            console.log('=== END FIELD MAPPING DEBUG ===');
            
            setSelectedFields(orderedFields);
        }
    }, [selectedReport, customFields]); // Only runs for cases reports due to early returns above

    useEffect(() => {
        selectedReportIdRef.current = selectedReport?.id ? String(selectedReport.id) : null;
        autoOpenedRunReportRef.current = false;
    }, [selectedReport]);

    useEffect(() => {
        return () => {
            if (hubFetchTimeoutRef.current) {
                clearTimeout(hubFetchTimeoutRef.current);
            }
            if (reportValuesAbortRef.current) {
                reportValuesAbortRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        // Check if this report was loaded from URL and data is ready
        const urlParams = new URLSearchParams(window.location.search);
        const reportId = urlParams.get('id');
        
        if (reportMode === 'builder' && reportId && 
            String(selectedReport?.id) === String(reportId) && 
            customFields && 
            customFields.length > 0 && 
            selectedStep < 3 &&
            !autoOpenedRunReportRef.current) {
            // All required data is loaded, jump to step 3 (Run Report)
            console.log('Data ready, jumping to Run Report step');
            autoOpenedRunReportRef.current = true;
            setSelectedStep(3);
        }
    }, [selectedReport, customFields, selectedStep, reportMode]);

    useEffect(() => {
        if (reportMode === 'builder' && selectedStep === 3) {
            runReportValuesFetch();
        }
    }, [selectedStep, reportMode]);

    useEffect(() => {
        if (reportMode !== 'hub' || !selectedReport || selectedFields.length === 0) {
            return;
        }

        const fieldsSignature = selectedFields.filter(Boolean).map((field) => ({
            id: normalizeFieldId(field.id),
            contactProperty: field.contactProperty || null,
            dropdownReturn: field.dropdownReturn || null,
            isBlank: Boolean(field.isBlank)
        }));
        const hubFetchKey = JSON.stringify({
            reportId: selectedReport.id,
            fields: fieldsSignature,
            criteria: selectedCriteria,
            perPage: pagination.per_page,
            sort: sortConfig
        });

        if (hubFetchKey === lastHubFetchKeyRef.current) {
            return;
        }

        setReportData(null);
        setLookupMaps({});
        setPagination((current) => ({
            ...current,
            current_page: 1,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false
        }));
        setFetchingReport(true);

        if (hubFetchTimeoutRef.current) {
            clearTimeout(hubFetchTimeoutRef.current);
        }

        hubFetchTimeoutRef.current = setTimeout(() => {
            lastHubFetchKeyRef.current = hubFetchKey;
            runReportValuesFetch(1, pagination.per_page);
            hubFetchTimeoutRef.current = null;
        }, 100);

        return () => {
            if (hubFetchTimeoutRef.current) {
                clearTimeout(hubFetchTimeoutRef.current);
                hubFetchTimeoutRef.current = null;
            }
        };
    }, [reportMode, selectedReport, selectedFields, selectedCriteria]);

    if (reportMode === 'hub') {
        return (
            <div className={`page-container full nm report-hub-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`} style={{ gap: '5px' }}>
                {/*<div className='report-hub-page-header'>
                    <h2>Reports</h2>
                    <span className='subtext'>View, export, share, and manage your reports here.</span>
                </div>*/}
                <div className='report-hub-layout'>
                    <ReportList
                        setSelectedReport={setSelectedReport}
                        selectedReport={selectedReport}
                        reports={reports}
                        onAIReportGenerate={handleAIReportGenerate}
                        onDeleteReport={deleteReport}
                        onSelectReport={selectReportForHub}
                        variant='hub'
                        headerActions={renderReportHubActions()}
                    />
                    <div className='report-hub-preview'>
                        {selectedReport ? (
                            <>
                                <div className='report-hub-preview-header'>
                                    <div className='report-hub-preview-title-row'>
                                        <div className='report-hub-preview-title-copy'>
                                            <h3>{selectedReport.name}</h3>
                                            <span className='subtext'>
                                                {isPublicFormReport
                                                    ? `${Number(selectedReport.submission_count || pagination.total || 0)} ${Number(selectedReport.submission_count || pagination.total || 0) === 1 ? 'submission' : 'submissions'} · Managed by Public Forms`
                                                    : reportData?.length
                                                        ? `${reportData.length} rows loaded`
                                                        : fetchingReport ? 'Loading report…' : 'Report preview'}
                                            </span>
                                        </div>
                                        <button
                                            type='button'
                                            className='report-hub-preview-close'
                                            onClick={closeReportHubPreview}
                                            aria-label={`Close ${selectedReport.name}`}
                                            title='Close report preview'
                                        >
                                            <X size={17} />
                                        </button>
                                    </div>
                                </div>
                                {pagination.total > 100 && (
                                    <div className='pagination-controls top'>
                                        <div className='pagination-info'>
                                            Showing {Math.max(1, (pagination.current_page - 1) * pagination.per_page + 1)} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
                                        </div>
                                        <div className='pagination-nav'>
                                            <button onClick={() => handlePageChange(1)} disabled={pagination.current_page === 1}>First</button>
                                            <button onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page === 1}>Previous</button>
                                            <span className='page-info'>Page {pagination.current_page} of {pagination.total_pages}</span>
                                            <button onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page === pagination.total_pages}>Next</button>
                                            <button onClick={() => handlePageChange(pagination.total_pages)} disabled={pagination.current_page === pagination.total_pages}>Last</button>
                                        </div>
                                    </div>
                                )}
                                <ReportView
                                    reportData={reportData}
                                    customFields={customFields}
                                    fetchingReport={fetchingReport}
                                    selectedFields={selectedFields}
                                    sortConfig={sortConfig}
                                    onSort={handleSort}
                                    lookupMaps={lookupMaps}
                                />
                            </>
                        ) : (
                            <div className='report-hub-empty'>
                                <h3>Select a report</h3>
                                <span className='subtext'>Choose a saved report from the list to preview it here, or create a new report.</span>
                                <div className='report-hub-empty-actions'>
                                    <button type='button' className='action tertiary' onClick={createReports}>
                                        <Plus size={18} />Create Report(s)
                                    </button>
                                    {canAccessPublicForms(accessLevel) && (
                                        <button type='button' className='action quaternary' onClick={() => openPublicForms()}>
                                            <ClipboardList size={18} />Build public form
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {selectedReport && !isPublicFormReport && (
                    <ReportSave
                        selectedReport={selectedReport}
                        selectedFields={selectedFields}
                        selectedCriteria={selectedCriteria}
                        saveReport={saveReport}
                        downloadExcel={downloadExcel}
                        downloadWord={downloadWord}
                        downloadCSV={downloadCSV}
                        refreshReports={fetchReports}
                        setMessage={setMessage}
                        initialAction={reportSaveInitialAction}
                        onInitialActionHandled={() => setReportSaveInitialAction(null)}
                        hideOptions
                    />
                )}
                {message && <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />}
            </div>
        );
    }

    return (
        <div className={`page-container height ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`} style={{ gap: '5px' }}>
            <div id='page-header' className='nm'>
                <h2>Reports</h2>
                <p className='subtext'>View and manage your reports here.</p>
            </div>
            <ReportProgress selectedStep={selectedStep} setSelectedStep={setSelectedStep} selectedReport={selectedReport} isListReport={isListReport} />
            {selectedReport && (
                <div className='selected-report-name'>
                    <div
                        className='selected-report-wrapper'
                        onClick={openRenameReportModal}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openRenameReportModal();
                            }
                        }}
                        role='button'
                        tabIndex={0}
                        title={Number(selectedReport.premade) === 1 ? 'Save this premade report before renaming' : 'Rename report'}
                    >
                        <h4>{selectedReport.name}</h4>
                        <X
                            size={18}
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedReport(null);
                            }}
                            style={{ cursor: 'pointer', color: "var(--text-color)" }}
                        />
                    </div>
                    {selectedStep === 4 && Number(selectedReport.premade) === 1 && (
                        <div className='selected-report-hint subtext'>
                            Save the report to view more options
                        </div>
                    )}
                    {reportData?.length > 1 && <span className='subtext'>{reportData?.length} rows returned</span>}
                </div>
            )}
            {selectedStep === 0 && <ReportList setSelectedReport={setSelectedReport} selectedReport={selectedReport} reports={reports} onAIReportGenerate={handleAIReportGenerate} onDeleteReport={deleteReport} onCreateListReport={createListReport} />}
            {selectedStep === 1 && <ReportColumns selectedReport={selectedReport} selectedFields={selectedFields} setSelectedFields={setSelectedFields} customFields={customFields} sections={sections} loadingCustomFields={loadingCustomFields} />}
            {selectedStep === 2 && (isListReport
                ? <ListReportCases selectedCriteria={selectedCriteria} setSelectedCriteria={setSelectedCriteria} />
                : <ReportCriteria customFields={customFields} sections={sections} selectedCriteria={selectedCriteria} setSelectedCriteria={setSelectedCriteria} loadingCustomFields={loadingCustomFields} />
            )}
            {selectedStep === 3 && (
                <div>
                    {pagination.total > 100 && (
                        <div className="pagination-controls top">
                            <div className="pagination-info">
                                Showing {Math.max(1, (pagination.current_page - 1) * pagination.per_page + 1)} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
                            </div>
                            <div className="pagination-per-page">
                                <select 
                                    value={pagination.per_page} 
                                    onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                >
                                    {perPageOptions.map(option => (
                                        <option key={option} value={option}>{option} per page</option>
                                    ))}
                                </select>
                            </div>
                            <div className="pagination-nav">
                                <button 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={pagination.current_page === 1}
                                >
                                    First
                                </button>
                                <button 
                                    onClick={() => handlePageChange(pagination.current_page - 1)} 
                                    disabled={pagination.current_page === 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {pagination.current_page} of {pagination.total_pages}
                                </span>
                                <button 
                                    onClick={() => handlePageChange(pagination.current_page + 1)} 
                                    disabled={pagination.current_page === pagination.total_pages}
                                >
                                    Next
                                </button>
                                <button 
                                    onClick={() => handlePageChange(pagination.total_pages)} 
                                    disabled={pagination.current_page === pagination.total_pages}
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    )}
                    <ReportView 
                        reportData={reportData} 
                        customFields={customFields} 
                        fetchingReport={fetchingReport} 
                        selectedFields={selectedFields} 
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        lookupMaps={lookupMaps}
                    />
                    {pagination.total > 100 && (
                        <div className="pagination-controls bottom">
                            <div className="pagination-info">
                                Showing {Math.max(1, (pagination.current_page - 1) * pagination.per_page + 1)} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
                            </div>
                            <div className="pagination-nav">
                                <button 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={pagination.current_page === 1}
                                >
                                    First
                                </button>
                                <button 
                                    onClick={() => handlePageChange(pagination.current_page - 1)} 
                                    disabled={pagination.current_page === 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {pagination.current_page} of {pagination.total_pages}
                                </span>
                                <button 
                                    onClick={() => handlePageChange(pagination.current_page + 1)} 
                                    disabled={pagination.current_page === pagination.total_pages}
                                >
                                    Next
                                </button>
                                <button 
                                    onClick={() => handlePageChange(pagination.total_pages)} 
                                    disabled={pagination.current_page === pagination.total_pages}
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {selectedStep === 4 && !isPublicFormReport && <ReportSave selectedReport={selectedReport} selectedFields={selectedFields} selectedCriteria={selectedCriteria} saveReport={saveReport} downloadExcel={downloadExcel} downloadWord={downloadWord} downloadCSV={downloadCSV} refreshReports={fetchReports} setMessage={setMessage} />}
            {showRenameReport && (
                <Modal
                    title="Rename Report"
                    onClose={() => setShowRenameReport(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button className='action alt' onClick={() => setShowRenameReport(false)}>
                                Close
                            </button>
                            <button className='action' onClick={commitRenameReport}>
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className='subtext'>Report Name<span className='required'>*</span></label>
                            <input
                                type='text'
                                className='form-control'
                                value={renameReportName}
                                onChange={(event) => setRenameReportName(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        commitRenameReport();
                                    }
                                }}
                                placeholder='Enter report name'
                                autoFocus
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {message && <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />}
        </div>
    );
};

export default Reports;
